import { Button, Col, Form, message, Popover, Row, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import API, { apis } from "common/apis";
import { IStoreRackUsers, IStoreRackUsersListAPI, IUserTableRackMappingUser } from "common/interface";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCities, getDarkStores } from "store/admin-slice/adminServicesSlice";

type RackRow = { key: string; rack: string; qcs: number[] };

const PAGE_SIZE = 25;
const MAX_VISIBLE_USERS = 2;

const getErrorMessage = (error: any, fallback: string) => {
  const data = error?.response?.data;
  return (
    data?.errors?.map((e: { message?: string }) => e.message).filter(Boolean).join(", ") ||
    data?.message ||
    fallback
  );
};

const RackUsersMappingPage = () => {
  useDocumentTitle("Rack Users mappings");
  const dispatch = useAppDispatch();
  const { darkStores, cities } = useAppSelector((state) => state.adminServices);

  const [cityId, setCityId] = useState<string>();
  const [storeId, setStoreId] = useState<string>();
  const [pageNo, setPageNo] = useState(1);
  const [list, setList] = useState<IStoreRackUsersListAPI>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [storeUsers, setStoreUsers] = useState<IUserTableRackMappingUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  // Store whose QC users were requested; makes the users API a one-time call per store.
  const usersRequestedFor = useRef<string>();
  const [editingKey, setEditingKey] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(getDarkStores());
    dispatch(getCities());
  }, [dispatch]);

  useEffect(() => {
    setPageNo(1);
    setList(undefined);
    setStoreUsers([]);
    usersRequestedFor.current = undefined;
  }, [storeId]);

  const fetchList = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const response = await API.get(apis.storeRackUsers, {
        params: { storeId, pageNo, pageSize: PAGE_SIZE },
      });
      setList(response.data);
    } catch (error: any) {
      message.error(getErrorMessage(error, "Unable to load rack users"));
    } finally {
      setLoading(false);
    }
  }, [storeId, pageNo]);

  useEffect(() => {
    setEditingKey("");
    fetchList();
  }, [fetchList]);

  const loadStoreUsers = async () => {
    if (!storeId || usersRequestedFor.current === storeId) return;
    usersRequestedFor.current = storeId;
    setUsersLoading(true);
    try {
      const response = await API.get(apis.getStoreRackQcUsers, { params: { storeId } });
      setStoreUsers(response.data ?? []);
    } catch (e) {
      usersRequestedFor.current = undefined;
      message.error("Unable to load QC users");
    } finally {
      setUsersLoading(false);
    }
  };

  const rows: RackRow[] = useMemo(
    () =>
      (list?.data ?? []).map((item: IStoreRackUsers) => ({
        key: item.rack,
        rack: item.rack,
        qcs: (item.qcs ?? []).map((u) => u.userId),
      })),
    [list]
  );

  // Users already assigned are included so their labels show before the users API loads.
  const userOptions = useMemo(() => {
    const map = new Map<number, string>();
    (list?.data ?? []).forEach((item) =>
      (item.qcs ?? []).forEach((u) => map.set(u.userId, u.label || `${u.phone}-${u.name}`))
    );
    storeUsers.forEach((u) => map.set(u.userId, u.label || `${u.phone}-${u.name}`));
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [list, storeUsers]);

  const isEditing = (record: RackRow) => record.key === editingKey;

  const onEdit = (record: RackRow) => {
    form.setFieldsValue({ qcs: record.qcs });
    setEditingKey(record.key);
  };

  const onCancel = () => {
    setEditingKey("");
    form.resetFields();
  };

  const onSave = async (record: RackRow) => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }
    setSaving(true);
    try {
      // Body: { storeId, racks: [{ rack, qcs: userId[] }] } (backend replaces the rack's QC assignment).
      await API.post(apis.storeRackUsers, {
        storeId: Number(storeId),
        racks: [{ rack: record.rack, qcs: values.qcs ?? [] }],
      });
      message.success(`Rack ${record.rack} updated`);
      setEditingKey("");
      fetchList();
    } catch (error: any) {
      message.error(getErrorMessage(error, "Unable to save rack users"));
    } finally {
      setSaving(false);
    }
  };

  const renderUser = (v: number | string) => {
    const label = String(userOptions.find((o) => o.value === v)?.label ?? v);
    const i = label.indexOf("-");
    const phone = i > 0 ? label.slice(0, i) : "";
    const name = i > 0 ? label.slice(i + 1) : label;
    return (
      <Tooltip key={v} title={label}>
        <div
          style={{
            background: "#f5f7fa",
            border: "1px solid #e8ebf0",
            borderRadius: 6,
            padding: "2px 8px",
            lineHeight: 1.3,
            overflow: "hidden",
          }}
        >
          <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name}
          </div>
          {phone && <div style={{ fontSize: 11, color: "#8c8c8c" }}>{phone}</div>}
        </div>
      </Tooltip>
    );
  };

  // Currently assigned users first; based on the value at edit start so the list doesn't jump while selecting.
  const withSelectedFirst = <T extends { value: number | string }>(options: T[], selected: (number | string)[] = []) => [
    ...options.filter((o) => selected.includes(o.value)),
    ...options.filter((o) => !selected.includes(o.value)),
  ];

  const columns: ColumnsType<RackRow> = [
    {
      key: "rack",
      title: "Rack",
      dataIndex: "rack",
      width: 120,
      render: (rack: string) => <Typography.Text strong>{rack}</Typography.Text>,
    },
    {
      key: "qcs",
      title: "QC",
      dataIndex: "qcs",
      render: (value: number[], record) => {
        if (isEditing(record)) {
          return (
            <Form.Item name="qcs" style={{ margin: 0 }}>
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Select QC users"
                optionFilterProp="label"
                options={withSelectedFirst(userOptions, value)}
                loading={usersLoading}
                onDropdownVisibleChange={(open) => open && loadStoreUsers()}
                maxTagCount="responsive"
                dropdownMatchSelectWidth={false}
                style={{ width: "100%" }}
              />
            </Form.Item>
          );
        }
        if (!value?.length) return <Typography.Text type="secondary">—</Typography.Text>;
        const extra = value.slice(MAX_VISIBLE_USERS);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 260 }}>
            {value.slice(0, MAX_VISIBLE_USERS).map(renderUser)}
            {extra.length > 0 && (
              <Popover
                trigger="click"
                title={`${extra.length} more`}
                content={
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200 }}>
                    {extra.map(renderUser)}
                  </div>
                }
              >
                <Tag color="blue" style={{ cursor: "pointer", alignSelf: "flex-start", margin: 0 }}>
                  +{extra.length} more
                </Tag>
              </Popover>
            )}
          </div>
        );
      },
    },
    {
      key: "action",
      title: "Action",
      width: 160,
      render: (_, record) =>
        isEditing(record) ? (
          <Space>
            <Button type="primary" size="small" loading={saving} onClick={() => onSave(record)}>
              Save
            </Button>
            <Button size="small" disabled={saving} onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        ) : (
          <Button size="small" disabled={editingKey !== ""} onClick={() => onEdit(record)}>
            Edit
          </Button>
        ),
    },
  ];

  const filterOption = (input: string, option?: { children?: unknown }) =>
    option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false;

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Rack Users mappings" />
        </Col>
      </Row>

      <Row gutter={24} style={{ marginBottom: "1rem" }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Typography.Text strong>City</Typography.Text>
          <Select
            showSearch
            placeholder="Select city"
            style={{ width: "100%", marginTop: 4 }}
            value={cityId}
            filterOption={filterOption}
            onChange={(value) => {
              setCityId(value);
              setStoreId(undefined);
            }}
          >
            {(cities ?? []).map((city) => (
              <Select.Option value={city.id} key={city.id}>
                {city.name} || {city.id}
              </Select.Option>
            ))}
          </Select>
        </Col>
        {cityId && (
          <Col xs={24} sm={12} md={8} lg={6}>
            <Typography.Text strong>Store ID</Typography.Text>
            <Select
              showSearch
              placeholder="Select store"
              style={{ width: "100%", marginTop: 4 }}
              value={storeId}
              filterOption={filterOption}
              onChange={setStoreId}
            >
              {(darkStores ?? [])
                .filter((store) => store?.cityId?.toString() === cityId?.toString())
                .map((store) => (
                  <Select.Option value={store.id} key={store.id}>
                    {store.name} || {store.id}
                  </Select.Option>
                ))}
            </Select>
          </Col>
        )}
      </Row>

      {storeId ? (
        <Form form={form} component={false}>
          <Table
            columns={columns}
            dataSource={rows}
            loading={loading}
            rowKey="key"
            rowClassName={(record) => (isEditing(record) ? "ant-table-row-selected" : "")}
            pagination={{
              current: pageNo,
              pageSize: PAGE_SIZE,
              total: list?.total ?? 0,
              showSizeChanger: false,
              onChange: setPageNo,
            }}
            scroll={{ x: 600, y: "65vh" }}
            size="middle"
            bordered
          />
        </Form>
      ) : (
        <Typography.Text type="secondary">Select a city and store to view rack users.</Typography.Text>
      )}
    </>
  );
};

export default RackUsersMappingPage;
